import {
	type Brand,
	CONTRACT_OPTIONS,
	FIELD_CATALOGUE,
	type FormFieldConfig,
	QUALIFICATION_OPTIONS,
	TELEWORK_OPTIONS,
} from "@shared/brand.types";
import { type FormEvent, useState } from "react";
import { Button } from "@/components/ds/Button";
import { copy } from "@/lib/copy";
import { fetchJson, HttpError } from "@/lib/http";
import { type CompanyIdentity, CompanyPicker } from "./CompanyPicker";
import { FormField, FormSection } from "./FormField";

type Errors = Record<string, string>;

/**
 * Le 422 du serveur, lu sans confiance : `{ errors: [{ field, message }] }`.
 *
 * Rien ne garantit la forme d'un corps d'erreur (un proxy peut en substituer un
 * autre), et une entrée mal formée ferait tomber le formulaire au moment précis où
 * l'entreprise vient de tout saisir. Une forme inattendue rend donc `null`, et
 * l'appelant retombe sur son message d'échec général.
 */
function erreursDeChamp(error: unknown): Errors | null {
	if (!(error instanceof HttpError) || error.status !== 422) return null;
	const corps = error.failure.kind === "http" ? error.failure.body : null;
	const liste = (corps as { errors?: unknown } | null)?.errors;
	if (!Array.isArray(liste)) return null;

	const paires = liste
		.filter(
			(item): item is { field: string; message: string } =>
				typeof item?.field === "string" && typeof item?.message === "string",
		)
		.map((item) => [item.field, item.message] as const);
	return paires.length > 0 ? Object.fromEntries(paires) : null;
}

const OPTIONS: Record<string, readonly string[]> = {
	contract: CONTRACT_OPTIONS,
	telework: TELEWORK_OPTIONS,
	qualification: QUALIFICATION_OPTIONS,
};

function BrandedField({
	config,
	value,
	error,
	onChange,
}: {
	config: FormFieldConfig;
	value: string;
	error?: string;
	onChange: (value: string) => void;
}) {
	const spec = FIELD_CATALOGUE[config.key];
	const id = `field-${config.key}`;
	const options = OPTIONS[config.key];
	const invalid = Boolean(error);
	// Le refus du serveur s'écrit sous le champ (`FormField`, id `<id>-aide`) : relié
	// ici, il est lu avec le champ au lieu de flotter seul dans la page.
	const a11y = {
		"aria-invalid": invalid,
		"aria-required": config.required || undefined,
		"aria-describedby": invalid ? `${id}-aide` : undefined,
	};

	return (
		<FormField
			id={id}
			label={config.label ?? spec.label}
			required={config.required}
			error={error}
			wide={spec.type === "textarea"}
		>
			{options ? (
				<select
					id={id}
					className="mg-field"
					{...a11y}
					value={value}
					onChange={(event) => onChange(event.target.value)}
				>
					<option value="">Sélectionner…</option>
					{options.map((option) => (
						<option key={option} value={option}>
							{option}
						</option>
					))}
				</select>
			) : spec.type === "textarea" ? (
				<textarea
					id={id}
					rows={6}
					className="mg-field resize-y"
					{...a11y}
					placeholder="Les missions, le profil recherché, l'équipe…"
					value={value}
					onChange={(event) => onChange(event.target.value)}
				/>
			) : (
				<input
					id={id}
					type={spec.type === "number" ? "number" : spec.type === "date" ? "date" : "text"}
					className="mg-field"
					{...a11y}
					value={value}
					onChange={(event) => onChange(event.target.value)}
				/>
			)}
		</FormField>
	);
}

/**
 * Le formulaire est CONSTRUIT à partir de la configuration de l'école.
 *
 * Ajouter un champ pour une école n'est donc pas un développement : c'est une ligne
 * de configuration. Et le serveur revalide la même configuration, parce qu'un
 * formulaire de navigateur ne prouve rien.
 */
export function OfferRequestForm({
	brand,
	onSubmitted,
	compact = false,
	initialValues,
	initialCompany,
}: {
	brand: Brand;
	onSubmitted: () => void;
	compact?: boolean;
	/** Ce qu'une lecture d'annonce a rempli d'avance. L'entreprise relit et corrige. */
	initialValues?: Record<string, string>;
	/**
	 * L'entreprise déjà connue, quand le dépôt part de l'espace : elle a été choisie
	 * dans l'annuaire au dépôt précédent, la refaire chercher serait lui redemander
	 * ce qu'on affiche à l'écran d'à côté.
	 */
	initialCompany?: CompanyIdentity | null;
}) {
	const [values, setValues] = useState<Record<string, string>>(initialValues ?? {});
	// L'identité d'entreprise vient de l'annuaire, pas de la frappe : elle a sa propre
	// forme (six champs liés entre eux) et n'a rien à faire dans le sac à texte.
	const [company, setCompany] = useState<CompanyIdentity | null>(
		initialCompany ?? (initialValues?.companyName ? { name: initialValues.companyName } : null),
	);
	const [errors, setErrors] = useState<Errors>({});
	const [busy, setBusy] = useState(false);
	const [failed, setFailed] = useState<string | null>(null);

	const active = brand.formFields.filter((field) => field.enabled);
	const set = (key: string) => (value: string) =>
		setValues((current) => ({ ...current, [key]: value }));

	async function submit(event: FormEvent) {
		event.preventDefault();
		setBusy(true);
		setErrors({});
		setFailed(null);

		const fields: Record<string, string> = {};
		for (const field of active) fields[field.key] = values[field.key] ?? "";

		try {
			await fetchJson(`/api/requests/${brand.slug}`, {
				method: "POST",
				body: {
					companyName: company?.name ?? "",
					siren: company?.siren ?? null,
					siret: company?.siret ?? null,
					address: company?.address ?? null,
					postalCode: company?.postalCode ?? null,
					city: company?.city ?? null,
					contactName: values.contactName ?? null,
					contactEmail: values.contactEmail ?? "",
					contactPhone: values.contactPhone ?? null,
					website: values.website ?? "",
					fields,
				},
			});
			onSubmitted();
		} catch (error) {
			// Le 422 est le refus CHAMP PAR CHAMP du serveur (« Le SIREN compte 9
			// chiffres »), et il se rend sous les champs concernés, pas dans le bandeau
			// d'échec. C'est pour lui que `HttpFailure` porte le corps entier.
			const refus = erreursDeChamp(error);
			if (refus) {
				setErrors(refus);
				// Ramener l'œil sur la première erreur : sur dix champs, elle peut être hors écran.
				document.querySelector('[aria-invalid="true"]')?.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});
				return;
			}
			// Le serveur a répondu non sans détailler, ou il n'a pas répondu du tout : les
			// deux se disent différemment, l'un demande de réessayer, l'autre de vérifier
			// sa connexion.
			const repondu = error instanceof HttpError && error.status !== null;
			setFailed(repondu ? copy.deposit.sendFailed : copy.deposit.sendFailedOffline);
		} finally {
			setBusy(false);
		}
	}

	return (
		<form onSubmit={submit} className="flex flex-col gap-10">
			<FormSection title={copy.deposit.companySection}>
				<CompanyPicker value={company} error={errors.companyName} onChange={setCompany} />

				<FormField id="contactName" label={copy.deposit.contactName}>
					<input
						id="contactName"
						className="mg-field"
						value={values.contactName ?? ""}
						onChange={(event) => set("contactName")(event.target.value)}
					/>
				</FormField>

				<FormField
					id="contactEmail"
					label={copy.deposit.contactEmail}
					required
					hint={copy.deposit.contactEmailHint}
					error={errors.contactEmail}
				>
					<input
						id="contactEmail"
						type="email"
						className="mg-field"
						aria-invalid={Boolean(errors.contactEmail)}
						aria-required="true"
						aria-describedby="contactEmail-aide"
						value={values.contactEmail ?? ""}
						onChange={(event) => set("contactEmail")(event.target.value)}
					/>
				</FormField>
			</FormSection>

			<FormSection title={copy.deposit.requestSection}>
				{active.map((config) => (
					<BrandedField
						key={config.key}
						config={config}
						value={values[config.key] ?? ""}
						error={errors[config.key]}
						onChange={set(config.key)}
					/>
				))}
			</FormSection>

			{/* Piège à robots : invisible pour un humain, irrésistible pour un script. */}
			<input
				type="text"
				name="website"
				tabIndex={-1}
				autoComplete="off"
				aria-hidden="true"
				className="absolute left-[-9999px] h-px w-px"
				value={values.website ?? ""}
				onChange={(event) => set("website")(event.target.value)}
			/>

			{failed ? (
				<p role="alert" className="t-body-md text-mg-error-fg">
					{failed}
				</p>
			) : null}

			<div className={compact ? "" : "border-t border-mg-border pt-8"}>
				<Button tone="primary" full type="submit" disabled={busy}>
					{busy ? copy.deposit.submitting : copy.deposit.submit}
				</Button>
			</div>
		</form>
	);
}
